import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, doc, getDoc, query, orderBy, addDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, Storage } from '../firebase';
import { ToastContainer, toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import 'react-toastify/dist/ReactToastify.css';
import '../style/Transaction.css';

const GSTCreditNotes = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [isAddingTransaction, setIsAddingTransaction] = useState(false);
    const [isEditingTransaction, setIsEditingTransaction] = useState(false);
    const [transactionToEdit, setTransactionToEdit] = useState(null);
    const [isCreatingCreditNote, setIsCreatingCreditNote] = useState(false);
    // Add these state variables at the top with other state variables
    const [isSearchingOrder, setIsSearchingOrder] = useState(false);
    const [searchOrderId, setSearchOrderId] = useState('');
    const [selectedBrandDetails, setSelectedBrandDetails] = useState(null);
    const [creditNoteData, setCreditNoteData] = useState({
        billTo: {
            name: '',
            address: '',
            state: '',
            pincode: '',
            gstNumber: ''
        },
        items: [
            {
                description: '',
                hsnCode: '',
                quantity: 1,
                rate: 0,
                igstPercent: 5,
                amount: 0
            }
        ],
        shippingCharge: 0,
        shippingIgst: 18,
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        taxType: 'igst' // Add this new field with default value 'igst'
    });
    
    // Using refs for form inputs instead of controlled components
    const typeInputRef = useRef(null);
    const amountInputRef = useRef(null);
    const balanceBeforeInputRef = useRef(null);
    const balanceAfterInputRef = useRef(null);
    const timestampInputRef = useRef(null);

    // Fetch all brands and their wallet details
    const fetchBrands = async () => {
        setLoading(true);
        try {
            const usersRef = collection(db, 'users');
            const usersSnapshot = await getDocs(usersRef);

            const brandDetails = [];

            for (const userDoc of usersSnapshot.docs) {
                const userId = userDoc.id;
                const walletRef = doc(db, 'users', userId, 'wallet', 'walletDetails');
                const walletSnap = await getDoc(walletRef);

                if (walletSnap.exists()) {
                    const walletData = walletSnap.data();
                    brandDetails.push({
                        userId,
                        brandName: userDoc.data().brandName,
                        balance: walletData.balance,
                        lastUpdated: walletData.lastUpdated.toDate().toLocaleString(),
                    });
                }
            }

            setBrands(brandDetails);
        } catch (error) {
            console.error('Error fetching brands:', error);
            toast.error('Failed to fetch brands. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch transactions for a selected brand
    const fetchTransactions = async (userId) => {
        setLoading(true);
        try {
            const transactionsCollection = collection(db, 'users', userId, 'wallet', 'walletDetails', 'transactions');
            const transactionsSnapshot = await getDocs(query(transactionsCollection, orderBy('timestamp', 'desc')));

            const allTransactions = transactionsSnapshot.docs.map((doc) => ({
                transactionId: doc.id,
                ...doc.data(),
            }));

            setTransactions(allTransactions);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            toast.error('Failed to fetch transactions. Please try again.');
        } finally {
            setLoading(false);
        }
    };


    const determineTaxType = (state) => {
        // Normalize state name for comparison
        const normalizedState = state.toLowerCase().trim();
        return normalizedState === 'maharashtra' ? 'cgst_sgst' : 'igst';
    };

    // Handle the click of "Details" to show the brand's transaction history
    const handleDetailsClick = async (userId) => {
        setSelectedBrand(userId);
        fetchTransactions(userId);
        
        // Fetch user details for pre-filling credit note
        try {
            const userRef = doc(db, 'users', userId);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
                setSelectedBrandDetails(userSnap.data());
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
        }
    };

    // Format date for datetime-local input
    const formatDateForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Handle Add Transaction button click
    const handleAddTransaction = () => {
        setIsAddingTransaction(true);
        setIsEditingTransaction(false);
        setIsCreatingCreditNote(false);
        setTransactionToEdit(null);
        
        // Clear form inputs
        if (typeInputRef.current) typeInputRef.current.value = '';
        if (amountInputRef.current) amountInputRef.current.value = '';
        if (balanceBeforeInputRef.current) balanceBeforeInputRef.current.value = '';
        if (balanceAfterInputRef.current) balanceAfterInputRef.current.value = '';
        if (timestampInputRef.current) timestampInputRef.current.value = formatDateForInput(new Date());
    };

    // Handle Create Credit Note button click
    // const handleCreateCreditNote = () => {
    //     setIsSearchingOrder(true);
    //     setIsCreatingCreditNote(true);
    //     setIsAddingTransaction(false);
    //     setIsEditingTransaction(false);
    //     setTransactionToEdit(null);
        
    //     // Generate a new invoice number with current date
    //     const today = new Date();
    //     const invoiceNumber = `CN/${today.getFullYear()}-${today.getFullYear()+1}/${Math.floor(Math.random() * 10000)}`;
        
    //     // Pre-fill with brand details if available
    //     setCreditNoteData({
    //         ...creditNoteData,
    //         billTo: {
    //             name: selectedBrandDetails?.brandName || '',
    //             address: selectedBrandDetails?.address || '',
    //             state: selectedBrandDetails?.state || '',
    //             pincode: selectedBrandDetails?.pincode || '',
    //             gstNumber: selectedBrandDetails?.gstNumber || ''
    //         },
    //         invoiceNumber,
    //         invoiceDate: today.toISOString().split('T')[0]
    //     });
    // };


    // Replace the existing handleCreateCreditNote function
const handleCreateCreditNote = () => {
    setIsSearchingOrder(true);
    setIsCreatingCreditNote(true);
    setIsAddingTransaction(false);
    setIsEditingTransaction(false);
    setTransactionToEdit(null);
    
    // Generate a new invoice number with current date
    const today = new Date();
    const invoiceNumber = `CN/${today.getFullYear()}-${today.getFullYear()+1}/${Math.floor(Math.random() * 10000)}`;
    
    // Determine tax type based on brand's state
    const taxType = selectedBrandDetails?.state ? determineTaxType(selectedBrandDetails.state) : 'igst';
    
    // Pre-fill with brand details if available
    setCreditNoteData({
        ...creditNoteData,
        billTo: {
            name: selectedBrandDetails?.brandName || '',
            address: selectedBrandDetails?.address || '',
            state: selectedBrandDetails?.state || '',
            pincode: selectedBrandDetails?.pincode || '',
            gstNumber: selectedBrandDetails?.gstNumber || ''
        },
        invoiceNumber,
        invoiceDate: today.toISOString().split('T')[0],
        taxType // Set tax type based on state
    });
};

    // Handle Edit Transaction button click
    const handleEditClick = (transaction) => {
        setIsEditingTransaction(true);
        setIsAddingTransaction(false);
        setIsCreatingCreditNote(false);
        setTransactionToEdit(transaction.transactionId);
        
        // Convert Firestore timestamp to Date object for the input
        const timestampDate = transaction.timestamp instanceof Timestamp 
            ? transaction.timestamp.toDate() 
            : new Date(transaction.timestamp.seconds * 1000);
        
        // Format date to YYYY-MM-DDThh:mm
        const formattedDate = formatDateForInput(timestampDate);
        
        // Set values to the refs
        setTimeout(() => {
            if (typeInputRef.current) typeInputRef.current.value = transaction.type || '';
            if (amountInputRef.current) amountInputRef.current.value = transaction.amount?.toString() || '';
            if (balanceBeforeInputRef.current) balanceBeforeInputRef.current.value = transaction.balanceBefore?.toString() || '';
            if (balanceAfterInputRef.current) balanceAfterInputRef.current.value = transaction.balanceAfter?.toString() || '';
            if (timestampInputRef.current) timestampInputRef.current.value = formattedDate;
        }, 0);
    };



    // Add this function to fetch order by ID
const fetchOrderById = async (orderId) => {
    setLoading(true);
    try {
        // Search in all users' collections since we might not know which user has this order
        const usersSnapshot = await getDocs(collection(db, 'users'));
        
        for (const userDoc of usersSnapshot.docs) {
            const userId = userDoc.id;
            const orderRef = doc(db, 'users', userId, 'orders', orderId);
            const orderSnap = await getDoc(orderRef);
            
            if (orderSnap.exists()) {
                const orderData = orderSnap.data();
                return {
                    ...orderData,
                    orderId,
                    userId
                };
            }
        }
        
        toast.error('Order not found');
        return null;
    } catch (error) {
        console.error('Error fetching order:', error);
        toast.error('Failed to fetch order');
        return null;
    } finally {
        setLoading(false);
    }
};


    // Save new transaction to Firestore
    const saveTransaction = async () => {
        // Get values from refs
        const type = typeInputRef.current?.value || '';
        const amountStr = amountInputRef.current?.value || '';
        const balanceBeforeStr = balanceBeforeInputRef.current?.value || '';
        const balanceAfterStr = balanceAfterInputRef.current?.value || '';
        const timestampStr = timestampInputRef.current?.value || '';
        
        if (!type || !amountStr || !balanceBeforeStr || !balanceAfterStr || !timestampStr) {
            toast.error('Please fill in all fields');
            return;
        }

        // Convert to proper types
        const amount = parseFloat(amountStr);
        const balanceBefore = parseFloat(balanceBeforeStr);
        const balanceAfter = parseFloat(balanceAfterStr);
        const timestamp = new Date(timestampStr);
        
        // Validate numeric values
        if (isNaN(amount) || isNaN(balanceBefore) || isNaN(balanceAfter) || isNaN(timestamp.getTime())) {
            toast.error('Please enter valid values for all fields');
            return;
        }

        try {
            setLoading(true);
            
            const transactionData = {
                type,
                amount,
                balanceBefore,
                balanceAfter,
                timestamp: Timestamp.fromDate(timestamp),
            };

            if (isEditingTransaction) {
                // Update existing transaction
                const transactionRef = doc(
                    db, 
                    'users', 
                    selectedBrand, 
                    'wallet', 
                    'walletDetails', 
                    'transactions', 
                    transactionToEdit
                );
                
                await updateDoc(transactionRef, transactionData);
                toast.success('Transaction updated successfully');
            } else {
                // Add new transaction
                const transactionsCollection = collection(
                    db, 
                    'users', 
                    selectedBrand, 
                    'wallet', 
                    'walletDetails', 
                    'transactions'
                );
                
                await addDoc(transactionsCollection, transactionData);
                toast.success('Transaction added successfully');
            }

            // Refresh transactions
            fetchTransactions(selectedBrand);
            
            // Update the wallet balance for the brand
            const walletRef = doc(db, 'users', selectedBrand, 'wallet', 'walletDetails');
            await updateDoc(walletRef, {
                balance: balanceAfter,
                lastUpdated: Timestamp.now()
            });
            
            // Refresh brands to show updated balance
            fetchBrands();
            
            // Reset form
            setIsAddingTransaction(false);
            setIsEditingTransaction(false);
            setTransactionToEdit(null);
            
        } catch (error) {
            console.error('Error saving transaction:', error);
            toast.error('Failed to save transaction. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle credit note data change
    const handleCreditNoteChange = (field, value, index = null, subfield = null) => {
        setCreditNoteData(prevData => {
            const newData = { ...prevData };
            
            if (index !== null && subfield !== null) {
                // Handle nested item fields
                const updatedItems = [...newData.items];
                updatedItems[index] = {
                    ...updatedItems[index],
                    [subfield]: value
                };
                
                // Recalculate amount if quantity or rate changes
                // if (subfield === 'quantity' || subfield === 'rate' || subfield === 'igstPercent') {
                //     const quantity = subfield === 'quantity' ? value : updatedItems[index].quantity;
                //     const rate = subfield === 'rate' ? value : updatedItems[index].rate;
                //     const igstPercent = subfield === 'igstPercent' ? value : updatedItems[index].igstPercent;
                    
                //     const baseAmount = quantity * rate;
                //     const igstAmount = baseAmount * (igstPercent / 100);
                //     updatedItems[index].amount = baseAmount + igstAmount;
                // }


                if (subfield === 'quantity' || subfield === 'rate' || subfield === 'igstPercent') {
                    const quantity = subfield === 'quantity' ? value : updatedItems[index].quantity;
                    const rate = subfield === 'rate' ? value : updatedItems[index].rate;
                    const igstPercent = subfield === 'igstPercent' ? value : updatedItems[index].igstPercent;
                    
                    const baseAmount = quantity * rate;
                    
                    if (newData.taxType === 'igst') {
                        const igstAmount = baseAmount * (igstPercent / 100);
                        updatedItems[index].amount = baseAmount + igstAmount;
                    } else { // cgst_sgst
                        const cgstPercent = igstPercent / 2;
                        const sgstPercent = igstPercent / 2;
                        const totalTax = baseAmount * ((cgstPercent + sgstPercent) / 100);
                        updatedItems[index].amount = baseAmount + totalTax;
                    }
                }
                
                newData.items = updatedItems;
            } else if (field === 'billTo') {
                // Handle billTo object updates
                newData.billTo = {
                    ...newData.billTo,
                    [value.field]: value.value
                };
            } else {
                // Handle direct field updates
                newData[field] = value;
            }
            
            return newData;
        });
    };

    // Add new item to the credit note
    const addItemToCreditNote = () => {
        setCreditNoteData(prevData => ({
            ...prevData,
            items: [
                ...prevData.items,
                {
                    description: '',
                    hsnCode: '',
                    quantity: 1,
                    rate: 0,
                    igstPercent: 5,
                    amount: 0
                }
            ]
        }));
    };

    // Remove item from credit note
    const removeItemFromCreditNote = (index) => {
        setCreditNoteData(prevData => {
            const newItems = [...prevData.items];
            newItems.splice(index, 1);
            return {
                ...prevData,
                items: newItems
            };
        });
    };

    // Generate credit note PDF
    const generateCreditNotePDF = () => {
        const doc = new jsPDF();
        const brandName = brands.find(brand => brand.userId === selectedBrand)?.brandName || 'Brand';
        
        // Add title
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.text('CREDIT NOTE', 105, 20, { align: 'center' });
        
        // Add brand information
        doc.setFontSize(10);
        doc.setFont("Helvetica", "normal");
        doc.text("UNITEE STUDIOS PRIVATE LIMITED", 10, 20);
        doc.text("Maharashtra, India", 10, 25);
        doc.text("GSTIN 27AADCU3575G1Z5", 10, 30);
        doc.text("rajprit@uniteeculture.com", 10, 35);
        doc.text("1ST FLOOR, PLOT 304/E/, 3, WAKHARIA BUILDING Vithalbhai Patel Road", 10, 40);
        doc.text("MUMBAI", 10, 45);
        
        // Add credit note details
        doc.setFont('helvetica', 'bold');
        doc.text('Credit Note Details', 140, 35);
        doc.setFont('helvetica', 'normal');
        doc.text(`Credit Note Number: ${creditNoteData.invoiceNumber}`, 140, 42);
        doc.text(`Credit Note Date: ${creditNoteData.invoiceDate}`, 140, 49);
        
        // Add bill to section
        doc.setFont('helvetica', 'bold');
        doc.text('Bill To:', 20, 65);
        doc.setFont('helvetica', 'normal');
        doc.text(`Name: ${creditNoteData.billTo.name}`, 20, 72);
        doc.text(`Address: ${creditNoteData.billTo.address}`, 20, 79);
        doc.text(`State: ${creditNoteData.billTo.state}`, 20, 86);
        doc.text(`Pin Code: ${creditNoteData.billTo.pincode}`, 20, 93);
        doc.text(`GST Number: ${creditNoteData.billTo.gstNumber}`, 20, 100);
        
        // Add items table
        doc.setFont('helvetica', 'bold');
        doc.text('Item & Description', 20, 115);
        doc.text('HSN/SAC', 90, 115);
        doc.text('Qty', 125, 115);
        doc.text('Rate', 140, 115);
        // doc.text('IGST %', 160, 115);
        doc.text('Amount', 180, 115);
        
        doc.line(20, 117, 190, 117);
        
        // Add items
        doc.setFont('helvetica', 'normal');
        let y = 125;
        
        let totalAmount = 0;
        let totalTax = 0;
        


        if (creditNoteData.taxType === 'igst') {
            doc.text('IGST %', 160, 115);
        } else {
            doc.text('CGST %', 150, 115);
            doc.text('SGST %', 170, 115);
        }
        
        // Then, modify how items are displayed
        creditNoteData.items.forEach((item, index) => {
            doc.text(item.description, 20, y);
            doc.text(item.hsnCode, 90, y);
            doc.text(item.quantity.toString(), 125, y);
            doc.text(item.rate.toFixed(2), 140, y);
            
            if (creditNoteData.taxType === 'igst') {
                doc.text(`${item.igstPercent}%`, 160, y);
            } else {
                const halfPercent = item.igstPercent / 2;
                doc.text(`${halfPercent}%`, 150, y);
                doc.text(`${halfPercent}%`, 170, y);
            }
            
            doc.text(item.amount.toFixed(2), 190, y);
            
            y += 8;
            
            // Calculate totals
            const baseAmount = item.quantity * item.rate;
            let taxAmount;
            
            if (creditNoteData.taxType === 'igst') {
                taxAmount = baseAmount * (item.igstPercent / 100);
            } else {
                const halfPercent = item.igstPercent / 2;
                taxAmount = baseAmount * (halfPercent / 100 * 2); // CGST + SGST
            }
            
            totalAmount += baseAmount + taxAmount;
            totalTax += taxAmount;
        });
        
        doc.line(20, y, 190, y);
        y += 8;
        
        // Add shipping charges if applicable
        // if (creditNoteData.shippingCharge > 0) {
        //     const shippingTax = creditNoteData.shippingCharge * (creditNoteData.shippingIgst / 100);
        //     totalAmount += parseFloat(creditNoteData.shippingCharge) + shippingTax;
        //     totalTax += shippingTax;
            
        //     doc.text('Shipping Charge', 20, y);
        //     doc.text(creditNoteData.shippingCharge.toString(), 140, y);
        //     doc.text(`${creditNoteData.shippingIgst}%`, 160, y);
        //     doc.text((parseFloat(creditNoteData.shippingCharge) + shippingTax).toFixed(2), 180, y);
            
        //     y += 8;
        // }

        if (creditNoteData.shippingCharge > 0) {
            const shippingTax = creditNoteData.shippingCharge * (creditNoteData.shippingIgst / 100);
            totalAmount += parseFloat(creditNoteData.shippingCharge) + shippingTax;
            totalTax += shippingTax;
            
            doc.text('Shipping Charge', 20, y);
            doc.text(creditNoteData.shippingCharge.toString(), 140, y);
            
            if (creditNoteData.taxType === 'igst') {
                doc.text(`${creditNoteData.shippingIgst}%`, 160, y);
            } else {
                const halfPercent = creditNoteData.shippingIgst / 2;
                doc.text(`${halfPercent}%`, 150, y);
                doc.text(`${halfPercent}%`, 170, y);
            }
            
            doc.text((parseFloat(creditNoteData.shippingCharge) + shippingTax).toFixed(2), 180, y);
            
            y += 8;
        }
        
        // Add total
        doc.setFont('helvetica', 'bold');
        doc.text('Total:', 140, y);
        doc.text(totalAmount.toFixed(2), 180, y);
        
        y += 8;
        
        // Add total tax
        // doc.text('Total Tax:', 140, y);
        // doc.text(totalTax.toFixed(2), 180, y);
        
        y += 16;
        
        // Add signature
        doc.text('Authorized Signature', 160, y);
        
        doc.line(160, y+2, 190, y+2);
        
        // Add footer
        y += 20;
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(10);
        doc.text('Thanks for your business.', 105, y, { align: 'center' });
        
        return doc;
    };

    // Save credit note to Firebase
    const saveCreditNote = async () => {
        // Validate credit note data
        if (!creditNoteData.billTo.name || !creditNoteData.billTo.address || creditNoteData.items.length === 0) {
            toast.error('Please fill in all required fields');
            return;
        }
        
        // Validate items
        for (const item of creditNoteData.items) {
            if (!item.description || !item.hsnCode || item.quantity <= 0 || item.rate <= 0) {
                toast.error('Please fill in all item details correctly');
                return;
            }
        }
        
        try {
            setLoading(true);
            
            // Calculate total amount
            let totalAmount = 0;
            creditNoteData.items.forEach(item => {
                totalAmount += item.amount;
            });
            
            // Add shipping if applicable
            if (creditNoteData.shippingCharge > 0) {
                const shippingTotal = parseFloat(creditNoteData.shippingCharge) * (1 + creditNoteData.shippingIgst / 100);
                totalAmount += shippingTotal;
            }
            
            // Generate PDF
            const creditNotePdf = generateCreditNotePDF();
            const pdfBlob = creditNotePdf.output('blob');
            
            // Create a unique filename
            const creditNoteFileName = `credit_note_${creditNoteData.invoiceNumber.replace(/[/\\?%*:|"<>]/g, '-')}.pdf`;
            const brandFolderPath = `creditNotes/`;
            
            // Upload to Firebase Storage
            const creditNoteRef = ref(Storage, `${brandFolderPath}${creditNoteFileName}`);
            const uploadTask = uploadBytesResumable(creditNoteRef, pdfBlob);
            
            uploadTask.on(
                'state_changed',
                snapshot => {
                    // Handle upload progress here if needed
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    console.log(`Credit Note Upload is ${progress}% done`);
                },
                error => {
                    console.error('Error uploading credit note:', error);
                    toast.error('Error uploading credit note');
                    setLoading(false);
                },
                async () => {
                    try {
                        // After upload is complete, get the download URL
                        const creditNoteDownloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        console.log('Credit Note uploaded, download URL:', creditNoteDownloadURL);
                        
                        // Create transaction entry for this credit note
                        const transactionsCollection = collection(
                            db, 
                            'users', 
                            selectedBrand, 
                            'wallet', 
                            'walletDetails', 
                            'transactions'
                        );
                        
                        // Get current balance
                        const walletRef = doc(db, 'users', selectedBrand, 'wallet', 'walletDetails');
                        const walletSnap = await getDoc(walletRef);
                        
                        if (!walletSnap.exists()) {
                            throw new Error('Wallet details not found');
                        }
                        
                        const currentBalance = walletSnap.data().balance;
                        const newBalance = currentBalance + totalAmount;
                        
                        // Add transaction for credit note
                        await addDoc(transactionsCollection, {
                            type: 'Credit Note',
                            amount: totalAmount, // Negative amount as it's a deduction
                            balanceBefore: currentBalance,
                            balanceAfter: newBalance,
                            timestamp: Timestamp.now(),
                            searchOrderId,
                            creditNoteNumber: creditNoteData.invoiceNumber,
                            creditNoteURL: creditNoteDownloadURL,
                            CNtype: "GSTcreditNote",
                            creditNote: {
                                invoiceNumber: creditNoteData.invoiceNumber,
                                invoiceDate: creditNoteData.invoiceDate,
                                billTo: creditNoteData.billTo,
                                items: creditNoteData.items,
                                shippingCharge: creditNoteData.shippingCharge,
                                shippingIgst: creditNoteData.shippingIgst,
                                totalAmount: totalAmount,
                            }
                        });
                        
                        // Update wallet balance
                        await updateDoc(walletRef, {
                            balance: newBalance,
                            lastUpdated: Timestamp.now()
                        });
                        
                        toast.success('Credit note created successfully');
                        
                        // Refresh transactions
                        fetchTransactions(selectedBrand);
                        
                        // Refresh brands to show updated balance
                        fetchBrands();
                        
                        // Reset form
                        setIsCreatingCreditNote(false);
                        
                    } catch (err) {
                        console.error('Error handling credit note transaction:', err);
                        toast.error('Error creating credit note transaction');
                    } finally {
                        setLoading(false);
                    }
                }
            );
            
        } catch (error) {
            console.error('Error creating credit note:', error);
            toast.error('Failed to create credit note. Please try again.');
            setLoading(false);
        }
    };

    // Cancel add/edit mode
    const cancelAddEdit = () => {
        setIsAddingTransaction(false);
        setIsEditingTransaction(false);
        setIsCreatingCreditNote(false);
        setTransactionToEdit(null);
    };

    useEffect(() => {
        fetchBrands();
    }, []);


    // Add this function to handle the order search
    const handleOrderSearch = async () => {
        if (!searchOrderId) {
            toast.error('Please enter an Order ID');
            return;
        }
        
        const orderData = await fetchOrderById(searchOrderId);
        if (orderData) {
            // Transform order items into credit note items
            const creditNoteItems = orderData.order_items.map(item => ({
                description: item.sku || 'NA',
                hsnCode: '998599',
                quantity: item.units || 1,
                rate: item.itemTotalPrice || 0,
                igstPercent: 5,
                amount: (item.quantity || 1) * (item.itemTotalPrice || 0) * 1.05
            }));
            
            // Get customer info
            const customerInfo = orderData.customer_info || {};
            
            // Generate invoice number
            const today = new Date();
            const invoiceNumber = `CN/${today.getFullYear()}-${today.getFullYear()+1}/${Math.floor(Math.random() * 10000)}`;
            
            // Set credit note data, but keep pre-filled brand details
            setCreditNoteData({
                billTo: {
                    // Use brand details instead of customer info for billing
                    name: selectedBrandDetails?.brandName || '',
                    address: selectedBrandDetails?.address || '',
                    state: selectedBrandDetails?.state || '',
                    pincode: selectedBrandDetails?.pincode || '',
                    gstNumber: selectedBrandDetails?.gstNumber || ''
                },
                items: creditNoteItems.length > 0 ? creditNoteItems : [{
                    description: '',
                    hsnCode: '',
                    quantity: 1,
                    rate: 0,
                    igstPercent: 5,
                    amount: 0
                }],
                shippingCharge : orderData.UniteeShipping - (orderData.UniteeShipping * 0.18),
                shippingIgst: 18,
                invoiceNumber,
                invoiceDate: today.toISOString().split('T')[0],
                orderId: searchOrderId
            });
            
            setIsSearchingOrder(false);
            setIsCreatingCreditNote(true);
        }
    };

    // Transaction form component
    const TransactionForm = () => (
        <div className="transaction-form">
            <h3>{isEditingTransaction ? 'Edit Transaction' : 'Add New Transaction'}</h3>
            <div className="form-group">
                <label>Transaction Type:</label>
                <input
                    type="text"
                    ref={typeInputRef}
                    placeholder="e.g., Deposit, Withdrawal"
                    required
                />
            </div>
            <div className="form-group">
                <label>Amount (₹):</label>
                <input
                    type="text"
                    ref={amountInputRef}
                    placeholder="Amount"
                    required
                />
            </div>
            <div className="form-group">
                <label>Balance Before (₹):</label>
                <input
                    type="text"
                    ref={balanceBeforeInputRef}
                    placeholder="Balance Before"
                    required
                />
            </div>
            <div className="form-group">
                <label>Balance After (₹):</label>
                <input
                    type="text"
                    ref={balanceAfterInputRef}
                    placeholder="Balance After"
                    required
                />
            </div>
            <div className="form-group">
                <label>Date and Time:</label>
                <input
                    type="datetime-local"
                    ref={timestampInputRef}
                    required
                />
            </div>
            <div className="form-buttons">
                <button onClick={saveTransaction}>
                    {isEditingTransaction ? 'Update' : 'Save'}
                </button>
                <button onClick={cancelAddEdit}>Cancel</button>
            </div>
        </div>
    );

    const OrderSearchModal = () => (
        <div className="order-search-modal">
            <h3>Search Order</h3>
            <div className="form-group">
                <label>Order ID:</label>
                <input
                    type="text"
                    value={searchOrderId}
                    onChange={(e) => setSearchOrderId(e.target.value)}
                    placeholder="Enter Order ID"
                />
            </div>
            <div className="form-buttons">
                <button onClick={handleOrderSearch}>Search</button>
                <button onClick={() => setIsSearchingOrder(false)}>Cancel</button>
            </div>
        </div>
    );

    // Credit Note Form component
    const CreditNoteForm = () => (
        <div className="credit-note-form">
            <h3>Create Credit Note</h3>

            {/* <div className="credit-note-section">
    <h4>Tax Type</h4>
    <div className="form-group">
        <label>Select Tax Type:</label>
        <select 
            value={creditNoteData.taxType}
            onChange={(e) => handleCreditNoteChange('taxType', e.target.value)}
        >
            <option value="igst">IGST</option>
            <option value="cgst_sgst">CGST & SGST</option>
        </select>
    </div>
</div> */}
            
            <div className="credit-note-section">
                <h4>Credit Note Details</h4>
                <div className="form-group">
                    <label>Credit Note Number:</label>
                    <input
                        type="text"
                        value={creditNoteData.invoiceNumber}
                        onChange={(e) => handleCreditNoteChange('invoiceNumber', e.target.value)}
                        required
                    />
                </div>
                <div className="form-group">
                    <label>Credit Note Date:</label>
                    <input
                        type="date"
                        value={creditNoteData.invoiceDate}
                        onChange={(e) => handleCreditNoteChange('invoiceDate', e.target.value)}
                        required
                    />
                </div>
            </div>
            
            <div className="credit-note-section">
                <h4>Bill To</h4>
                <div className="form-group">
                    <label>Name:</label>
                    <input
                        type="text"
                        value={creditNoteData.billTo.name}
                        onChange={(e) => handleCreditNoteChange('billTo', { field: 'name', value: e.target.value })}
                        required
                    />
                </div>
                <div className="form-group">
                    <label>Address:</label>
                    <textarea
                        value={creditNoteData.billTo.address}
                        onChange={(e) => handleCreditNoteChange('billTo', { field: 'address', value: e.target.value })}
                        required
                    />
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label>State:</label>
                        <input
                            type="text"
                            value={creditNoteData.billTo.state}
                            onChange={(e) => handleCreditNoteChange('billTo', { field: 'state', value: e.target.value })}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Pin Code:</label>
                        <input
                            type="text"
                            value={creditNoteData.billTo.pincode}
                            onChange={(e) => handleCreditNoteChange('billTo', { field: 'pincode', value: e.target.value })}
                            required
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label>GST Number:</label>
                    <input
                        type="text"
                        value={creditNoteData.billTo.gstNumber}
                        onChange={(e) => handleCreditNoteChange('billTo', { field: 'gstNumber', value: e.target.value })}
                    />
                </div>
            </div>
            
            <div className="credit-note-section">
                <h4>Items</h4>
                {creditNoteData.items.map((item, index) => (
                    <div key={index} className="item-entry">
                        <h5>Item {index + 1}</h5>
                        <div className="form-group">
                            <label>Description:</label>
                            <input
                                type="text"
                                value={item.description}
                                onChange={(e) => handleCreditNoteChange('items', e.target.value, index, 'description')}
                                required
                            />
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>HSN/SAC:</label>
                                <input
                                    type="text"
                                    value={item.hsnCode}
                                    onChange={(e) => handleCreditNoteChange('items', e.target.value, index, 'hsnCode')}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Quantity:</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => handleCreditNoteChange('items', parseInt(e.target.value) || 0, index, 'quantity')}
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Rate (₹):</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={item.rate}
                                    onChange={(e) => handleCreditNoteChange('items', parseFloat(e.target.value) || 0, index, 'rate')}
                                    required
                                />
                            </div>
                            {/* <div className="form-group">
                                <label>IGST %:</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={item.igstPercent}
                                    onChange={(e) => handleCreditNoteChange('items', parseFloat(e.target.value) || 0, index, 'igstPercent')}
                                    required
                                />
                            </div> */}
                        </div>
                        <div className="form-group">
                        <label>Amount (₹):</label>
                            <input
                                type="number"
                                step="0.01"
                                value={item.amount}
                                readOnly
                            />
                        </div>
                        {creditNoteData.items.length > 1 && (
                            <button type="button" className="remove-item-btn" onClick={() => removeItemFromCreditNote(index)}>
                                Remove Item
                            </button>
                        )}
                        <hr />
                    </div>
                ))}
                <button type="button" className="add-item-btn" onClick={addItemToCreditNote}>
                    Add Another Item
                </button>
            </div>
            
            <div className="credit-note-section">
                <h4>Shipping</h4>
                <div className="form-row">
                    <div className="form-group">
                        <label>Shipping Charge (₹):</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={creditNoteData.shippingCharge}
                            onChange={(e) => handleCreditNoteChange('shippingCharge', parseFloat(e.target.value) || 0)}
                        />
                    </div>
                    <div className="form-group">
                        <label>Tax:</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={creditNoteData.shippingIgst}
                            onChange={(e) => handleCreditNoteChange('shippingIgst', parseFloat(e.target.value) || 0)}
                        />
                    </div>
                </div>
            </div>
            
            <div className="form-buttons">
                <button onClick={saveCreditNote}>Create Credit Note</button>
                <button onClick={cancelAddEdit}>Cancel</button>
            </div>
        </div>
    );

    // View Credit Note component
    const ViewCreditNote = ({ transaction }) => {
        if (!transaction.creditNote) return null;
        
        return (
            <div className="credit-note-preview">
                <h4>Credit Note: {transaction.creditNote.invoiceNumber}</h4>
                <p>Date: {transaction.creditNote.invoiceDate}</p>
                <p>Bill To: {transaction.creditNote.billTo.name}</p>
                <p>Total Amount: ₹{transaction.creditNote.totalAmount.toFixed(2)}</p>
                <div className="credit-note-actions">
                    <a href={transaction.creditNoteURL} target="_blank" rel="noopener noreferrer" className="download-link">
                        Download Credit Note
                    </a>
                </div>
            </div>
        );
    };


    return (
        <div className="transactions-page">

           {isSearchingOrder && <OrderSearchModal />}
            <ToastContainer position="top-center" autoClose={5000} hideProgressBar />
            {loading ? (
                <p>Loading...</p>
            ) : (
                <>
                    {!selectedBrand ? (
                        <div>
                            <h2>Brands Overview</h2>
                            <table className="brands-table">
                                <thead>
                                    <tr>
                                        <th>Brand Name</th>
                                        <th>Balance (₹)</th>
                                        <th>Last Updated</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {brands.length > 0 ? (
                                        brands.map((brand) => (
                                            <tr key={brand.userId}>
                                                <td>{brand.brandName}</td>
                                                <td>{brand.balance.toFixed(2)}</td>
                                                <td>{brand.lastUpdated}</td>
                                                <td>
                                                    <button onClick={() => handleDetailsClick(brand.userId)}>
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4">No brands found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div>


                            
                            <h2>Transaction Details for {brands.find(brand => brand.userId === selectedBrand)?.brandName}</h2>
                            <div className="transaction-controls">
                                <button onClick={() => setSelectedBrand(null)}>Back to Overview</button>
                                <button onClick={handleAddTransaction}>Add New Transaction</button>
                                <button onClick={handleCreateCreditNote}>Create Credit Note</button>
                            </div>
                            
                            {isAddingTransaction && <TransactionForm />}
                            {isCreatingCreditNote && <CreditNoteForm />}
                            
                            <table className="transactions-table">
                                <thead>
                                    <tr>
                                        <th>Transaction Type</th>
                                        <th>Amount (₹)</th>
                                        <th>Balance Before (₹)</th>
                                        <th>Balance After (₹)</th>
                                        <th>Date</th>
                                        <th>Credit Note</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.length > 0 ? (
                                        transactions.map((transaction) => (
                                            <tr key={transaction.transactionId}>
                                                <td>{transaction.type}</td>
                                                <td>{transaction.amount !== undefined && transaction.amount !== null ? transaction.amount.toFixed(2) : 'NA'}</td>
                                                <td>{transaction.balanceBefore !== undefined && transaction.balanceBefore !== null ? transaction.balanceBefore.toFixed(2) : 'NA'}</td>
                                                <td>{transaction.balanceAfter !== undefined && transaction.balanceAfter !== null ? transaction.balanceAfter.toFixed(2) : 'NA'}</td>
                                                <td>{new Date(transaction.timestamp.seconds * 1000).toLocaleString()}</td>
                                                <td>
                                                    {transaction && (
                                                        <a href={transaction.creditNoteURL} target="_blank" rel="noopener noreferrer">
                                                            {transaction.creditNoteNumber}
                                                        </a>
                                                    )}
                                                </td>
                                                <td>
    <button onClick={() => handleEditClick(transaction)}>Edit</button>
    <button onClick={() => {
        setIsSearchingOrder(true);
        setSelectedBrand(transaction.userId || selectedBrand);
    }}>Create Credit Note</button>
</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7">No transactions found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default GSTCreditNotes;